'use client';

import { tv } from 'tailwind-variants';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button/button.component';
import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';

interface EntityAboutProps {
  description: string;
  analyticsEvent: AnalyticsEnumKeyType;
}

const entityAboutStyle = tv({
  slots: {
    container: [' overflow-hidden transition-all duration-500 ease-in-out'],
    text: ['text-gray-700 break-words whitespace-pre-line line-clamp-5'],
  },

  variants: {
    showMore: {
      true: {
        text: ['line-clamp-none'],
      },
    },
  },
});

export function EntityAbout({ description, analyticsEvent }: EntityAboutProps) {
  const [showMore, setShowMore] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const { container, text } = entityAboutStyle();
  const analytics = getAnalyticsPlugin();

  const hasThan290 = description.length > 290;

  useEffect(() => {
    if (textRef.current && hasThan290) {
      const itemHeight = textRef.current.scrollHeight;

      setHeight(showMore ? itemHeight : 120);
    }
  }, [showMore, hasThan290]);

  function handleShowMoreToggle() {
    if (!showMore) {
      analytics.track(analyticsEvent);
    }

    setShowMore((prev) => !prev);
  }

  return (
    <section data-testid={DataTestIdEnum.ENTITY_SECTION_ABOUT}>
      <div ref={textRef} style={{ height: height ? `${height}px` : 'auto' }} className={container()}>
        <p className={text({ showMore })}>{description}</p>
      </div>

      {hasThan290 && (
        <Button
          variant="text"
          startIcon={showMore ? faMinus : faPlus}
          onClick={handleShowMoreToggle}
          startIconClassName={`w-[0.875rem] h-[0.875rem] transition-transform duration-300 ease-in-out z-0 ${
            showMore ? 'rotate-180' : 'rotate-0'
          }`}
          className="mt-2 text-green-500 hover:underline focus:outline-none transition-colors duration-300 ease-in-out text-left justify-start"
        >
          {showMore ? 'Recolher' : 'Ver mais'}
        </Button>
      )}
    </section>
  );
}
